import { React, useEffect, useState, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import "./index.scss";

// import scannerIcon from "../../assets/images/common/scanner.svg";

import SideNav from "../../components/PostLogin/SideNav";
import ProfileAvatar from '../../components/ui/ProfileAvatar';
import ScanHistoryTab from "../../components/PostLogin/UserDetails/ScanHistory";
import AssetsTab from "../../components/PostLogin/DomainDetails/AssetsTab";
import DeleteDomainModal from "../../components/PostLogin/Domain/DeleteDomainModal";
import SideModal from '../../components/ui/SideModal';
import { toaster } from "../../components/ui/Toaster";
import SkeletonLoader from "../../components/ui/SkeletonLoader/index.js";

import ScanProgressModal from '../../components/PostLogin/DomainDetails/ScanProgressModal';
import EditDomainModal from '../../components/PostLogin/Domain/EditDomainModal'; // Import the EditDomainModal

import { editIcon, deleteIcon, headerArrowRightIcon } from "../../utils/icons.js";
import { formatDate, getDomainImageUrl, getUserImageUrl } from "../../utils/util.js";

import { useSelector, useDispatch } from 'react-redux';
import { getDomainDetails, resetDomainDetails, resetUpdateDomainResponse, getServerList } from '../../store/actions/domainAction.js';

const DomainDetails = () => {

    const domainDetailResponse = useSelector((state) => state.domain?.domainDetails);
    const deleteDomainResponse = useSelector((state) => state.domain?.deleteDomainResponse);
    const updateDomainResponse = useSelector((state) => state.domain?.updateDomainResponse);

    const dispatch = useDispatch();

    const { t } = useTranslation();

    const { domainName } = useParams();

    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('scanHistory');
    const [modalShow, setModalShow] = useState(false);
    const [showScanProgressModal, setShowScanProgressModal] = useState(false);
    const [pausedDomainId, setPausedDomainId] = useState(null);
    const [showAddOrgSidePanel, setShowAddOrgSidePanel] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [domainDetails, setDomainDetails] = useState({});

    const closeAddOrgSidePanel = () => {
        setShowAddOrgSidePanel(false)
    }

    const showModal = () => {
        setModalShow(true);
    };

    const fetchDomainDetails = useCallback(()=>{
        let obj = {
            domain: domainName
        }
        setIsLoading(true);
        dispatch(getDomainDetails(obj));
    },[dispatch, domainName])

    // const openScanProgressModal = () => {
    //     setShowScanProgressModal(true);
    // };

    const closeScanProgressModal = () => {
        setShowScanProgressModal(false);
    };

    const handlePauseResume = (id) => {
        setPausedDomainId((prev) => (prev === id ? null : id));
    };

    const backTodomainListPage = () => {
        navigate(`/domain`)
    }
    const fetchServerList = useCallback(()=>{
        dispatch(getServerList())
    },[dispatch])

    useEffect(()=>{
        fetchDomainDetails()
    },[fetchDomainDetails])


    useEffect(()=>{
        if(domainDetailResponse != null){
            setIsLoading(false);
            setDomainDetails(domainDetailResponse)
            dispatch(resetDomainDetails())
        }
    },[domainDetailResponse, dispatch])

    useEffect(()=>{
        if(deleteDomainResponse != null) {
            if(deleteDomainResponse?.detail?.status === "success") {
                setModalShow(false)
                navigate(`/domain`)
            } else {
                setModalShow(false)
                toaster.addToast(deleteDomainResponse?.description, "error")
            }
        }
    },[deleteDomainResponse, dispatch, navigate])

    useEffect(()=>{
        if(updateDomainResponse != null) {
            closeAddOrgSidePanel()
            fetchDomainDetails()
            if(updateDomainResponse?.detail?.status === "success") {
                toaster.addToast(updateDomainResponse?.detail?.description, "success");
            } else {
                toaster.addToast(updateDomainResponse?.description, "error");
            }
            dispatch(resetUpdateDomainResponse())
        }
    },[updateDomainResponse, dispatch, fetchDomainDetails])

    return (
        <div className="organization-details-container">
            <SideNav />
            <div className="organization-page-container">
                <div className="page-header-container px-4 py-3 d-flex align-items-center justify-content-between">
                    <div className="header-left d-flex align-items-center gap-3">
                        <h1 onClick={()=>{backTodomainListPage()}}>{t("domainPage.heading")}</h1>
                        {headerArrowRightIcon()}
                        {domainDetails !== null && <ProfileAvatar type="domain" name={domainDetails?.domain} imageUrl={getDomainImageUrl(domainDetails)} />}
                    </div>
                    <div className="header-right d-flex gap-3">
                        {/* <button className='viewScan-btn' onClick={openScanProgressModal}>
                            <img src={scannerIcon} alt="edit" className="d-inline-block mx-1" />
                            {t("domainDetailsPage.viewScanBtn")}
                        </button> */}
                        <button onClick={() => { 
                            setShowAddOrgSidePanel(true)
                            fetchServerList()
                        }}> {/* Edit button to open modal */}
                            <p className="d-inline-block mx-1">{editIcon()}</p>
                            {t("organizationDetailsPage.editBtn")}
                        </button>
                        <button onClick={() => showModal()}>
                            <p className="d-inline-block mx-1">{deleteIcon()}</p>
                            {t("organizationDetailsPage.deleteBtn")}
                        </button>
                    </div>
                </div>
                <div className="page-main-section p-4">
                    <div className="organization-created d-flex justify-content-between">
                        <h4>{t("domainDetailsPage.heading")}</h4>
                        {domainDetails?.created_by && <ProfileAvatar pageType={"organizationDetails"} type="profile" firstName={domainDetails?.created_by?.name} lastName={""} imageUrl={getUserImageUrl(domainDetails?.created_by)} />}
                    </div>
                    <div className="organization-header-container p-3 my-3 rounded">
                        {
                            isLoading ?
                            <SkeletonLoader type={"line"} count={3} currentPage={"domainDetails"} />
                            :
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="d-flex justify-content-between">
                                    <div className="header-details d-flex gap-3">
                                        <p>{t("organizationDetailsPage.createdOn")} <span>{formatDate(domainDetails?.created_at)}</span></p>
                                        {domainDetails?.updated_at !== null && <p>{t("organizationDetailsPage.updatedOn")} <span>{formatDate(domainDetails?.updated_at)}</span></p>}
                                        {domainDetails?.last_scan_detail?.created_at !== null && <p>{t("domainDetailsPage.lastScanOn")} <span>{formatDate(domainDetails?.last_scan_detail?.created_at)}</span></p>}
                                    </div>
                                </div>
                                <div className="d-flex gap-3 mt-3">
                                    <div className="hacker-score-container p-3 rounded text-center">
                                        <p className="hacker-score">{domainDetails?.stats?.score?.latest_score?.overall_score?.obtained_score}<span>/{domainDetails?.stats?.score?.latest_score?.overall_score?.max_score}</span></p>
                                        <p className="hacker-text">{t("organizationDetailsPage.latestHackerScore")}</p>
                                    </div>
                                    <p className="border-right mx-2"></p>
                                    <div className="total-score-container rounded">
                                        <div className="users-totol-count d-flex">
                                            <p className="total-user-count">{domainDetails?.stats?.scans?.total}</p>
                                            <div className="user-count mx-3 mt-2">
                                                <span className="active-icon d-inline-block mx-1"></span>
                                                <span className="passive-icon d-inline-block"></span>
                                                <p>{t("domainPage.totalScans")}</p>
                                            </div>
                                        </div>
                                        <div className="active-disabled-count d-flex gap-3">
                                            <p><span className="count d-inline-block">{domainDetails?.stats?.scans?.active}</span> {t("domainPage.activeScans")}</p>
                                            <p><span className="count-passive d-inline-block">{domainDetails?.stats?.scans?.passive}</span> {t("domainPage.passiveScans")}</p>
                                        </div>
                                    </div>
                                    <div className="total-domains-container rounded">
                                        <p className="total-domain">{domainDetails?.stats?.scans?.monitor}</p>
                                        <p className="domain-text">{t("domainDetailsPage.autoSchedule")}</p>
                                    </div>
                                </div>
                            </motion.div>
                        }                        
                    </div>
                    <div className="organization-main-container p-3">
                        <div className="orgs-tab-container d-flex rounded">
                            <p className={`rounded text-center ${activeTab === "scanHistory" ? "active" : ""}`} onClick={() => setActiveTab("scanHistory")}>{t("domainDetailsPage.scanTab")}</p>
                            <p className={`rounded text-center ${activeTab === "assets" ? "active" : ""}`} onClick={() => setActiveTab("assets")}>{t("domainDetailsPage.assetTab")}</p>
                        </div>
                        {activeTab === "scanHistory" && <ScanHistoryTab scanHistoryData={domainDetails} currentPageType={"domainDetails"} />}
                        {activeTab === "assets" && <AssetsTab doaminData={domainDetails}/>}
                    </div>
                    <DeleteDomainModal show={modalShow} onHide={() => setModalShow(false)} domainData={domainDetails}/>
                    {showScanProgressModal && (
                        <ScanProgressModal
                            show={showScanProgressModal}
                            onHide={closeScanProgressModal}
                            // scanningDomainId={scanningDomainId}
                            pausedDomainId={pausedDomainId}
                            handlePauseResume={handlePauseResume}
                        />
                    )}
                    <SideModal showSidePanel={showAddOrgSidePanel} hideSidePanel={closeAddOrgSidePanel}>
                        {showAddOrgSidePanel && <EditDomainModal domainData={domainDetails} cancelEditDoamin={closeAddOrgSidePanel}/>}
                    </SideModal>
                </div>
            </div>
        </div>
    );
};

export default DomainDetails;
